import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { DatePicker } from './ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { supabase } from '../lib/supabase';
import { useNotifications } from '../store/useStore';
import { addMonths, formatCurrency } from '../lib/subscriptionUtils';
import {
    AUTO_INVOICE_LEAD_DAYS,
    BILLING_PERIOD_OPTIONS,
    countUpcomingDrafts,
} from '../lib/subscriptionInvoiceUtils';
import { oggiRoma } from '../lib/dataItalia';
import { cn } from '../lib/cn';
import type { Database } from '../types/database.types';

type Customer = Database['public']['Tables']['customers']['Row'];

export interface CustomerSubscription {
    id: string;
    customer_id: string;
    monthly_amount: number;
    start_date: string;
    /** Scadenza del contratto: solo promemoria, le bozze continuano oltre. */
    next_renewal_date: string;
    renewal_period_months: number;
    /** Prossima fattura: primo giorno non ancora fatturato. */
    next_billing_date: string;
    billing_anchor_date: string | null;
    billing_period_months: number;
    auto_invoice: boolean;
    status: string;
    notes: string | null;
    customers?: { company_name: string; codice_cliente?: string | null } | null;
}

interface Props {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    subscription?: CustomerSubscription | null;
    defaultCustomerId?: string;
}

const schema = z.object({
    customer_id: z.string().min(1, 'Seleziona un cliente'),
    monthly_amount: z.coerce.number().min(0.01, "L'importo deve essere maggiore di 0"),
    renewal_period_months: z.coerce.number().int().min(1, 'Inserisci un periodo valido'),
    billing_period_months: z.coerce.number().int().min(1).max(12),
    auto_invoice: z.boolean(),
    notes: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const FORM_DEFAULTS: FormValues = {
    customer_id: '',
    monthly_amount: 0,
    renewal_period_months: 6,
    billing_period_months: 1,
    auto_invoice: true,
    notes: '',
};

export function SubscriptionFormModal({ open, onClose, onSaved, subscription, defaultCustomerId }: Props) {
    const { addNotification } = useNotifications();
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [renewalDate, setRenewalDate] = useState<Date | undefined>(undefined);
    const [billingDate, setBillingDate] = useState<Date | undefined>(undefined);
    const [submitting, setSubmitting] = useState(false);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerList, setShowCustomerList] = useState(false);
    const [selectedCustomerName, setSelectedCustomerName] = useState('');

    const isEditing = !!subscription;

    const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: FORM_DEFAULTS,
    });

    const renewalPeriod = watch('renewal_period_months');
    const billingMonths = Number(watch('billing_period_months')) || 1;
    const autoInvoice = watch('auto_invoice');
    const monthlyAmount = Number(watch('monthly_amount')) || 0;

    // Quante bozze preparerebbe il giro di stanotte con questi dati: se la
    // prossima fattura e' nel passato, una per ogni periodo arretrato.
    const billingISO = billingDate ? format(billingDate, 'yyyy-MM-dd') : null;
    const anchorForPreview =
        isEditing && billingISO === subscription?.next_billing_date
            ? subscription?.billing_anchor_date ?? billingISO
            : billingISO;
    const upcomingDrafts = autoInvoice && billingISO
        ? countUpcomingDrafts(billingISO, billingMonths, anchorForPreview)
        : 0;
    const isBillingInPast = !!billingISO && billingISO < oggiRoma();

    // Load customers
    useEffect(() => {
        if (!open) return;
        const fetchCustomers = async () => {
            setLoadingCustomers(true);
            const { data } = await supabase
                .from('customers')
                .select('id, company_name, codice_cliente')
                .eq('is_active', true)
                .order('company_name');
            if (data) setCustomers(data as Customer[]);
            setLoadingCustomers(false);
        };
        fetchCustomers();
    }, [open]);

    // Populate form when editing
    useEffect(() => {
        if (!open) return;
        if (subscription) {
            reset({
                customer_id: subscription.customer_id,
                monthly_amount: subscription.monthly_amount,
                renewal_period_months: subscription.renewal_period_months,
                billing_period_months: subscription.billing_period_months,
                auto_invoice: subscription.auto_invoice,
                notes: subscription.notes ?? '',
            });
            setStartDate(parseISO(subscription.start_date));
            setRenewalDate(parseISO(subscription.next_renewal_date));
            setBillingDate(parseISO(subscription.next_billing_date));
            setSelectedCustomerName(subscription.customers?.company_name ?? '');
        } else {
            reset({ ...FORM_DEFAULTS, customer_id: defaultCustomerId ?? '' });
            const today = new Date();
            setStartDate(today);
            setRenewalDate(addMonths(today, 6));
            setBillingDate(today);
            setSelectedCustomerName('');
        }
    }, [open, subscription, defaultCustomerId, reset]);

    // In creazione, scadenza e prima fattura seguono la data di inizio.
    useEffect(() => {
        if (!isEditing && startDate && renewalPeriod) {
            setRenewalDate(addMonths(startDate, Number(renewalPeriod)));
        }
    }, [startDate, renewalPeriod, isEditing]);

    useEffect(() => {
        if (!isEditing && startDate) setBillingDate(startDate);
    }, [startDate, isEditing]);

    const filteredCustomers = customers.filter(c =>
        c.company_name.toLowerCase().includes(customerSearch.toLowerCase())
    );

    const handleSelectCustomer = (customer: Customer) => {
        setValue('customer_id', customer.id, { shouldValidate: true });
        setSelectedCustomerName(customer.company_name);
        setCustomerSearch('');
        setShowCustomerList(false);
    };

    const onSubmit = async (values: FormValues) => {
        if (!startDate || !renewalDate || !billingDate) {
            addNotification({ type: 'error', title: 'Errore', message: 'Inserisci le date obbligatorie' });
            return;
        }
        setSubmitting(true);

        const nextBillingDate = format(billingDate, 'yyyy-MM-dd');
        // Il giorno di riferimento dei periodi cambia solo quando cambia la
        // prossima fattura: lasciarlo com'e' evita di scivolare a fine mese.
        const isBillingDateChanged = !isEditing || nextBillingDate !== subscription!.next_billing_date;

        const payload = {
            customer_id: values.customer_id,
            monthly_amount: values.monthly_amount,
            start_date: format(startDate, 'yyyy-MM-dd'),
            next_renewal_date: format(renewalDate, 'yyyy-MM-dd'),
            renewal_period_months: values.renewal_period_months,
            billing_period_months: values.billing_period_months,
            auto_invoice: values.auto_invoice,
            next_billing_date: nextBillingDate,
            ...(isBillingDateChanged ? { billing_anchor_date: nextBillingDate } : {}),
            notes: values.notes || null,
        };

        try {
            if (isEditing) {
                // Lo stato non si tocca: modificare un contratto annullato non
                // deve riattivarlo, altrimenti ripartirebbero le bozze.
                const { error } = await supabase
                    .from('customer_subscriptions')
                    .update(payload)
                    .eq('id', subscription!.id);
                if (error) throw error;
                addNotification({ type: 'success', title: 'Salvato', message: 'Subscription aggiornata' });
            } else {
                const { data: { user } } = await supabase.auth.getUser();
                const { error } = await supabase
                    .from('customer_subscriptions')
                    .insert({ ...payload, status: 'active', created_by: user?.id });
                if (error) throw error;
                addNotification({ type: 'success', title: 'Creata', message: 'Subscription aggiunta con successo' });
            }
            onSaved();
            onClose();
        } catch (err: any) {
            console.error(err);
            addNotification({ type: 'error', title: 'Errore', message: err.message ?? 'Impossibile salvare' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{isEditing ? 'Modifica Subscription' : 'Nuova Subscription'}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
                    {/* Cliente */}
                    {!defaultCustomerId && (
                        <div className="space-y-1 relative">
                            <Label>Cliente *</Label>
                            <Input
                                placeholder="Cerca cliente..."
                                value={showCustomerList ? customerSearch : selectedCustomerName}
                                onChange={(e) => {
                                    setCustomerSearch(e.target.value);
                                    setShowCustomerList(true);
                                }}
                                onFocus={() => setShowCustomerList(true)}
                                className={errors.customer_id ? 'border-red-500' : ''}
                                disabled={isEditing}
                                readOnly={isEditing}
                            />
                            {errors.customer_id && <p className="text-xs text-red-500">{errors.customer_id.message}</p>}
                            {showCustomerList && !isEditing && (
                                <div className="absolute z-50 w-full bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                                    {loadingCustomers ? (
                                        <div className="p-2 text-sm text-gray-500">Caricamento...</div>
                                    ) : filteredCustomers.length === 0 ? (
                                        <div className="p-2 text-sm text-gray-500">Nessun cliente trovato</div>
                                    ) : (
                                        filteredCustomers.map(c => (
                                            <button
                                                key={c.id}
                                                type="button"
                                                className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                                                onClick={() => handleSelectCustomer(c)}
                                            >
                                                <span className="font-medium">{c.company_name}</span>
                                                {c.codice_cliente && (
                                                    <span className="text-gray-400 ml-2 text-xs font-mono">{c.codice_cliente}</span>
                                                )}
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Importo mensile */}
                    <div className="space-y-1">
                        <Label>Importo mensile (€) *</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            {...register('monthly_amount')}
                            className={errors.monthly_amount ? 'border-red-500' : ''}
                        />
                        {errors.monthly_amount && <p className="text-xs text-red-500">{errors.monthly_amount.message}</p>}
                    </div>

                    {/* Contratto */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <Label>Data inizio *</Label>
                            <DatePicker date={startDate} setDate={setStartDate} placeholder="Seleziona data" />
                        </div>
                        <div className="space-y-1">
                            <Label>Durata contratto (mesi)</Label>
                            <Input
                                type="number"
                                min="1"
                                {...register('renewal_period_months')}
                                className={errors.renewal_period_months ? 'border-red-500' : ''}
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <Label>Scadenza contratto *</Label>
                        <DatePicker date={renewalDate} setDate={setRenewalDate} placeholder="Seleziona data" />
                        <p className="text-xs text-gray-400">
                            È un promemoria: le fatture continuano anche dopo, finché il contratto non viene annullato.
                        </p>
                    </div>

                    {/* Fatturazione */}
                    <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50/60 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Fatturazione</p>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="space-y-1">
                                <Label>Cadenza</Label>
                                <Select
                                    value={String(billingMonths)}
                                    onValueChange={(v) => setValue('billing_period_months', Number(v), { shouldDirty: true })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {BILLING_PERIOD_OPTIONS.map((o) => (
                                            <SelectItem key={o.months} value={String(o.months)}>
                                                {o.label} — {formatCurrency(monthlyAmount * o.months)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1">
                                <Label>Prossima fattura *</Label>
                                <DatePicker date={billingDate} setDate={setBillingDate} placeholder="Seleziona data" />
                            </div>
                        </div>
                        <p className="text-xs text-gray-400">
                            Ogni fattura: {formatCurrency(monthlyAmount * billingMonths)} di imponibile.
                            La prossima fattura è il primo giorno del periodo ancora da fatturare, e avanza da sola.
                        </p>

                        <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                            <input
                                type="checkbox"
                                {...register('auto_invoice')}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300"
                            />
                            <span>
                                <span className="font-medium">Prepara la bozza in automatico</span>
                                <span className="block text-xs text-gray-500">
                                    Ogni notte, {AUTO_INVOICE_LEAD_DAYS} giorni prima dell'inizio del periodo, nasce una
                                    bozza da controllare e mandare a Fatture in Cloud. Non viene mai emessa da sola.
                                </span>
                            </span>
                        </label>

                        {upcomingDrafts > 0 && (
                            <div
                                className={cn(
                                    'rounded border px-3 py-2 text-xs',
                                    isBillingInPast
                                        ? 'border-amber-200 bg-amber-50 text-amber-800'
                                        : 'border-gray-200 bg-white text-gray-600'
                                )}
                            >
                                {upcomingDrafts === 1
                                    ? 'Stanotte verrà preparata 1 bozza'
                                    : `Stanotte verranno preparate ${upcomingDrafts} bozze`}
                                {isBillingInPast ? ', una per ogni periodo arretrato.' : '.'}
                            </div>
                        )}
                    </div>

                    {/* Note */}
                    <div className="space-y-1">
                        <Label>Note / Piano</Label>
                        <textarea
                            {...register('notes')}
                            rows={3}
                            placeholder="Dettagli del piano, servizi inclusi, condizioni speciali..."
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
                        <Button type="submit" disabled={submitting}>
                            {submitting ? 'Salvataggio...' : isEditing ? 'Salva modifiche' : 'Crea subscription'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
